import React, { useEffect } from 'react'
import { Box, SvgIcon, IconButton, Typography } from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { useSupportAssistant } from '../support-assistant.hooks'
import { useDirection } from '@hapana/shared/web/hooks/useDirection'
import { TabNavigation } from './tab-navigation'
import { AssistantTab } from './assistant-tab'
import { SupportTab } from './support-tab'
import { DocsTab } from './docs-tab'
import { ActivityTab } from './activity-tab'
import { TicketForm } from './ticket-form'
import { SidebarHeader } from './sidebar-header'
import HapanaAiIcon from '@hapana/shared/web/styles/assets/svgs/hapana-ai-icon.svg'

const SupportSidebar: React.FC = () => {
  const { setIsOpen, activeTab, currentView, setCurrentView, userInfo } = useSupportAssistant()
  const { isRTL } = useDirection()
  const { t } = useTranslation('core')

  // Add escape key listener to close sidebar
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [setIsOpen])

  // Render content based on active tab
  const renderTabContent = () => {
    // Handle ticket view in assistant tab separately
    if (activeTab === 'chats' && currentView === 'ticket') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'grey.200',
              bgcolor: '#F8F9F9',
            }}
          >
            <IconButton size="small" onClick={() => setCurrentView('chat')} aria-label="Back">
              <ChevronLeft />
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('supportAssistant.emailSupport')}
            </Typography>
          </Box>
          <TicketForm
            userInfo={
              userInfo || {
                firstName: 'User',
                lastName: 'Name',
                email: 'user@example.com',
              }
            }
          />
        </Box>
      )
    }

    switch (activeTab) {
      case 'chats':
        return <AssistantTab />
      case 'support':
        return <SupportTab />
      case 'docs':
        return <DocsTab />
      case 'activity':
        return <ActivityTab />
      default:
        return <AssistantTab />
    }
  }

  return (
    <Box
      sx={{
        width: { xs: '100dvw', sm: 400 },
        maxWidth: { xs: '100dvw', sm: 400 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        borderLeft: !isRTL ? '1px solid' : undefined,
        borderRight: isRTL ? '1px solid' : undefined,
        borderColor: 'grey.200',
      }}
      data-testid="support-drawer"
    >
      {/* Main Header */}
      <SidebarHeader
        title={t('supportAssistant.title')}
        leftIcon={
          <SvgIcon
            component={HapanaAiIcon as any}
            sx={{ fontSize: '1.375rem', color: 'primary.main' }}
          />
        }
        onClose={() => setIsOpen(false)}
      />

      {/* Tab Navigation */}
      <TabNavigation />

      {/* Tab Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {renderTabContent()}
      </Box>
    </Box>
  )
}

export default SupportSidebar
